import useWebSocket from 'react-use-websocket';
import { WebSocketHook } from 'react-use-websocket/dist/lib/types';

import type {
  LeadUpdateArgs,
  PhotoUpdateArgs,
  BroadcoastToWebSocketClientsArgs,
  SetConnectionIdArgs,
  SpaceUpdateArgs,
} from '@backend/src/lib/websockets';
import { API_WEBSOCKET_ROOT } from 'utils';
import logger from 'logger';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import {
  handleWebsocketLeadUpdate,
  savePhotoToStore,
  saveSpaceToStore,
} from 'actions/leadActions';
import { setWebSocketConnectionId } from 'actions/appActions';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useWebsocket = (): WebSocketHook<unknown, MessageEvent<any> | null> => {
  const dispatch = useAppDispatch();
  const activeLeadID = useAppSelector((state) => state.app?.activeLeadID);

  const s = useWebSocket(
    activeLeadID ? `${API_WEBSOCKET_ROOT}?_id=${activeLeadID}` : null,
    {
      onOpen: async () => {
        // eslint-disable-next-line no-console
        console.info('WebSocket connection established.');

        s.sendJsonMessage({
          action: 'getConnectionId',
        });
      },
      onMessage: async (message) => {
        const { event, data } = JSON.parse(message.data) as Omit<
          BroadcoastToWebSocketClientsArgs,
          'leadId'
        >;

        logger.info(`WebSocket onMessage: ${event}`, { event, data });

        switch (event) {
          case 'SetConnectionId':
            dispatch(
              setWebSocketConnectionId(
                (data as SetConnectionIdArgs['data']).connectionId,
              ),
            );
            break;

          case 'LeadUpdate':
            dispatch(
              handleWebsocketLeadUpdate({
                partialLead: (data as LeadUpdateArgs['data']).updatedLead,
                connectionId: data.connectionId,
              }),
            );
            break;

          case 'SpaceUpdate':
            dispatch(
              saveSpaceToStore(
                (data as SpaceUpdateArgs['data']).updatedPhoto,
                (data as SpaceUpdateArgs['data']).newInventory,
              ),
            );
            break;

          case 'PhotoUpdate':
            dispatch(
              savePhotoToStore((data as PhotoUpdateArgs['data']).updatedPhoto),
            );
            break;

          default:
            break;
        }
      },
      onClose: () => {
        // eslint-disable-next-line no-console
        console.info('WebSocket connection closed.');
      },
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error('WebSocket connection error.', { error });
      },
      onReconnectStop: () => {
        // eslint-disable-next-line no-console
        console.info('WebSocket connection onReconnectStop.');
      },
      share: true,
      filter: () => false,
      retryOnError: true,
      shouldReconnect: () => true,
    },
  );

  return s;
};

export default useWebsocket;
